import qs from 'qs';
import { useState, useLayoutEffect } from 'react';
import { cloneDeep, forEach, isArray } from 'lodash';

export default (config: any) => {
	// historyHelper is here as we don't have a react dom history object to work with ( which causes a change to
	// propagate through the component tree ), we can use it to force a state change, which causes the component using
	// this hook to refresh.
	const [, setHistoryHelper] = useState(window.location.hash);

	// Respond to external changes to location.hash
	useLayoutEffect( () => {
		const onHashChanged = () => {
			setHistoryHelper(location.hash);
		};
		window.addEventListener("hashchange", onHashChanged);
		return () => {
			window.removeEventListener('hashchange', onHashChanged);
		}
	}, []);

	const {
		history = window.history,
		location = window.location,
		parameters = {},
		defaults = {},
	} = config;
	const replace = (newPath: string) => {
		if (typeof history.replace === 'function') {
			history.replace(newPath);
		} else if (typeof location.replace === 'function') {
			const path = newPath.replace(/^.*#/, '#');
			location.replace(path);
			setHistoryHelper(path);
		}
	};

	const normalizeState = (input: any, format = 'object') => {
		let normalized: any;
		if (typeof input === 'string') {
			normalized = qs.parse(input.replace(/^#!/, ''), {
				ignoreQueryPrefix: true,
			});
			forEach(parameters, (type, parameter) => {
				if (normalized.hasOwnProperty(parameter)) {
					switch (type) {
						case 'csv':
							normalized[parameter] = normalized[parameter].split(
								',',
							);
							break;
						case 'int':
							normalized[parameter] = parseInt(
								normalized[parameter],
								10,
							);
							break;
						case 'bool':
							normalized[parameter] = !!normalized[parameter];
							break;
						default:
							break;
					}
				}
			});
		} else {
			normalized = cloneDeep(input);
		}

		Object.keys(normalized).forEach((parameter) => {
			if (!parameters.hasOwnProperty(parameter)) {
				delete normalized[parameter];
			}
		});

		forEach(parameters, (type, parameter) => {
			if (normalized.hasOwnProperty(parameter)) {
				switch (type) {
					case 'int':
						if (isNaN(normalized[parameter])) {
							delete normalized[parameter];
						}
						break;
					case 'csv':
					case 'array':
						if (!isArray(normalized[parameter])) {
							delete normalized[parameter];
						}
						break;
					default:
						break;
				}
			}
			if (
				!normalized.hasOwnProperty(parameter) &&
				defaults.hasOwnProperty(parameter)
			) {
				normalized[parameter] = defaults[parameter];
			}
		});

		switch (format) {
			case 'query':
				forEach(parameters, (type, parameter) => {
					if (normalized.hasOwnProperty(parameter)) {
						switch (type) {
							case 'csv':
								normalized[parameter] = normalized[
									parameter
								].join(',');
								break;
							case 'bool':
								normalized[parameter] = normalized[parameter]
									? 1
									: 0;
								break;
							default:
								break;
						}
					}
				});
				Object.keys(normalized).forEach((key) => {
					if (!normalized[key]) {
						delete normalized[key];
					}
				});
				return '#!' + qs.stringify(normalized);
			case 'object':
			default:
				return normalized;
		}
	};

	const state = normalizeState(location.hash);
	const updateState = (next: any) => {
		const stringifiedState = normalizeState({ ...state, ...next }, 'query');
		if (stringifiedState !== location.hash) {
			replace(location.pathname + stringifiedState);
		}
	};	

	return [state, normalizeState(state, 'query'), updateState, normalizeState];
};
